import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDisputMessages, postDisputMessage, setDisputStatus, fetchWithAuth } from '../api';
import Layout from '../components/ui/Layout';
import { getCurrentUser, removeToken } from '../auth';
import Button from '../components/ui/Button';

interface DisputeChatPageProps {
  isClient?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const DisputeChatPage: React.FC<DisputeChatPageProps> = ({ isClient = false }) => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const leadIdNum = Number(leadId);

  const [messages, setMessages] = React.useState<any[]>([]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lead, setLead] = React.useState<any>(null);

  const load = React.useCallback(async () => {
    if (!leadIdNum) return;
    setLoading(true);
    setError(null);
    try {
      // Сначала грузим лид, чтобы понять открыт ли диспут
      const leadRes = await fetchWithAuth(`${API_URL}/leads/${leadIdNum}`);
      const leadData = await leadRes.json();
      setLead(leadData);

      // Для клиента: если диспут не открыт, сообщения не загружаем и скрываем
      if (isClient && !leadData?.disput_status) {
        setMessages([]);
      } else {
        const msgRes = await getDisputMessages(leadIdNum);
        setMessages(msgRes.data || msgRes || []);
      }
    } catch (e: any) {
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [leadIdNum, isClient]);

  React.useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!input.trim()) return;
    await postDisputMessage(leadIdNum, input.trim());
    setInput('');
    load();
  };

  const approve = async (approved: boolean) => {
    const status = approved ? 'Утверждено' as const : 'Не утверждено' as const;
    await setDisputStatus(leadIdNum, status);
    await load();
    navigate('/disputs');
  };

  const openByClient = async () => {
    await fetchWithAuth(`${API_URL}/disputs/${leadIdNum}/open`, { method: 'POST' });
    await load();
  };

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  const content = (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button variant="ghost" onClick={() => (isClient ? navigate('/client/disputs') : navigate('/disputs'))}>Назад к спорам</Button>
      </div>

      {loading ? (
        <div>Загрузка...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <div><b>Лид #{leadId}</b></div>
            <div>Статус спора: {lead?.disput_status || '—'}</div>
          </div>

          {isClient && !lead?.disput_status && (
            <div style={{ marginBottom: 12 }}>
              <Button variant="primary" onClick={openByClient}>Открыть диспут</Button>
            </div>
          )}

          {/* Сообщения и форма отправки показываем:
             - всегда для админов/менеджеров
             - для клиентов только когда спор уже открыт */}
          {(!isClient || !!lead?.disput_status) && (
            <>
              <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 12, background: '#fff', minHeight: 200 }}>
                {messages.length === 0 ? (
                  <div style={{ color: '#666' }}>Пока нет сообщений</div>
                ) : (
                  messages.map((m) => (
                    <div key={m.id} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: '#555' }}>{m.user_name || `user#${m.user_id}`} — {new Date(m.created_at).toLocaleString('ru-RU')}</div>
                      <div>{m.message}</div>
                    </div>
                  ))
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Сообщение..."
                  style={{ flex: 1, padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
                />
                <Button variant="primary" onClick={send}>Отправить</Button>
              </div>
            </>
          )}

          {!isClient && lead?.disput_status === 'На проверке' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => approve(true)} style={{ padding: '8px 12px', background: '#2e7d32', color: 'white', borderRadius: 4 }}>Возврат (утвердить)</button>
              <button onClick={() => approve(false)} style={{ padding: '8px 12px', background: '#c62828', color: 'white', borderRadius: 4 }}>Отказ</button>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isClient) {
    const user = getCurrentUser();
    return (
      <Layout user={user} onLogout={handleLogout}>
        {content}
      </Layout>
    );
  }

  return content;
};

export default DisputeChatPage; 